import { useContext, useState, Fragment } from "react";
import { Context } from "Context";
import FooterWrapper from "components/shared/FooterWrapper";
import Legal from "components/legal/Legal";
import Columns from "components/footer/Columns";
import SocialMediaItem from "components/footer/SocialMediaItem";
import RelatedDisneySites from "components/footer/RelatedDisneySites";
import SocialInformationItem from "components/footer/SocialInformationItem";
import SocialLeadGenItem from "components/footer/SocialLeadGenItem";
import DisneyReservationCenter from "components/footer/DisneyReservationCenter";
import PromotionDisclaimer from "components/footer/PromotionDisclaimer";
import MeansOfPayment from "components/footer/MeansOfPayment";
import FooterCallToAction from "components/footer/FooterCallToAction";
import Separator from "components/shared/Separator";
import Icon from "components/shared/Icon";
import { isCurrentPathPresent } from "utils";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import appConfig from "config.json";

const { mobileMediaQuery } = appConfig;

const mainClass = "syndicated-footer";
const syndicatedFooterContainerClass = `${mainClass}__container`;
const syndicatedFooterSocialMediaContainerClass = `${mainClass}__social-media-and-information-container`;

const Footer = () => {
	const {
		config: {
			brand,
			showRelatedDisneySites,
			locale,
			absoluteLinksFromBrand,
			theme,
		},
		footerData: {
			column,
			footer,
			labels,
			relatedDisneySites,
			meansOfPayment,
			promotionDisclaimer,
			planYourVacation,
		},
		profileData: { affiliations },
	} = useContext(Context);
	const [showOnMobile, setShowOnMobile] = useState(false);
	const isMobileView = useCheckMediaQuery(mobileMediaQuery);
	const mainComponentClass = `${mainClass} ${mainClass}--${brand} ${mainClass}--${locale}`;

	const renderSocialMediaAndInformationItem = (
		content,
		index,
		componentsToRender
	) => {
		if (["stayConnected", "disneyParks"].includes(content.id)) {
			componentsToRender.push(<SocialMediaItem {...content} key={index} />);
		} else if (content.id === "leadGen") {
			componentsToRender.push(<SocialLeadGenItem {...content} key={index} />);
		} else {
			componentsToRender.push(
				<SocialInformationItem
					{...content}
					key={index}
					affiliations={affiliations}
				/>
			);
		}
	};

	const renderSocialMediaAndInformationItems = () => {
		const componentsToRender = [];
		footer.modules?.forEach((content, index) => {
			if (content.modules) {
				const subComponents = [];
				content.modules.forEach((moduleContent, subIndex) => {
					renderSocialMediaAndInformationItem(
						moduleContent,
						subIndex,
						subComponents
					);
				});
				componentsToRender.push(
					<ul
						className={`${syndicatedFooterSocialMediaContainerClass}__group`}
						key={index}
					>
						{subComponents}
					</ul>
				);
			} else {
				renderSocialMediaAndInformationItem(content, index, componentsToRender);
			}
		});
		return componentsToRender;
	};

	const renderPlanYourVacation = () => {
		if (
			!planYourVacation ||
			!isCurrentPathPresent(planYourVacation.allowedPages) ||
			(theme &&
				!appConfig.brands[brand].footerCallToAction?.themes.includes(theme))
		) {
			return null;
		}
		return <FooterCallToAction {...planYourVacation} />;
	};

	return (
		<FooterWrapper>
			<div
				className={`${mainComponentClass} ${mainClass}--top`}
				style={{ visibility: "hidden" }}
			>
				{renderPlanYourVacation()}
				{labels && (
					<DisneyReservationCenter
						title={labels.drcCtaTitle}
						subtitle={labels.drcCtaSubtitle}
						footer={labels.drcCtaFooter}
					/>
				)}
				{promotionDisclaimer && (
					<PromotionDisclaimer
						{...promotionDisclaimer}
						absoluteLinksFromBrand={absoluteLinksFromBrand}
					/>
				)}
				{meansOfPayment && isCurrentPathPresent(meansOfPayment.allowedPages) && (
					<Fragment>
						<Separator section={`footer`} hideInDesktop />
						<MeansOfPayment {...meansOfPayment} />
						<Separator section={`footer`} />
					</Fragment>
				)}
				{(column || footer) && (
					<nav
						aria-label={labels.accessibiltyTextFooterLinks}
						className={`${syndicatedFooterContainerClass}${
							showOnMobile ? ` ${syndicatedFooterContainerClass}--show` : ""
						}${
							!relatedDisneySites || !showRelatedDisneySites
								? ` ${syndicatedFooterContainerClass}__content--without-related-disney-sites`
								: ""
						}`}
					>
						<button
							className={`${syndicatedFooterContainerClass}__button${
								showOnMobile
									? ` ${syndicatedFooterContainerClass}__button--active`
									: ""
							}`}
							onClick={() => setShowOnMobile(!showOnMobile)}
							aria-expanded={showOnMobile}
							tabIndex={0}
						>
							{isMobileView && (
								<Icon icon={"norgie-closed"} rotate={showOnMobile} />
							)}
							<span
								dangerouslySetInnerHTML={{
									__html: labels?.showMoreLinks || "",
								}}
							></span>
						</button>
						<div
							aria-hidden={isMobileView && !showOnMobile}
							className={`${syndicatedFooterContainerClass}__content ${syndicatedFooterContainerClass}__content${
								showOnMobile ? "--show" : ""
							}`}
						>
							{column && (
								<Columns
									columns={column.modules}
									accessibleText={labels?.accessibiltyTextFooterLinks}
									isMobileView={isMobileView}
									brand={brand}
									footer={footer}
								/>
							)}
							{footer && (
								<ul
									className={`${syndicatedFooterSocialMediaContainerClass} ${syndicatedFooterSocialMediaContainerClass}--${brand}--${locale}`}
								>
									{renderSocialMediaAndInformationItems()}
								</ul>
							)}
							{relatedDisneySites && showRelatedDisneySites && isMobileView && (
								<RelatedDisneySites
									title={relatedDisneySites.title}
									linksColumns={relatedDisneySites.modules}
								/>
							)}
						</div>
					</nav>
				)}
			</div>
			{isMobileView ? (
				<Legal wrap={false} />
			) : (
				<div className={`${mainComponentClass} ${mainClass}--bottom`}>
					{relatedDisneySites && showRelatedDisneySites && (
						<RelatedDisneySites
							title={relatedDisneySites.title}
							linksColumns={relatedDisneySites.modules}
						/>
					)}
					<Legal wrap={false} />
				</div>
			)}
		</FooterWrapper>
	);
};

export default Footer;
